import { Component, Input, OnInit } from '@angular/core';
import { AccountSettingsService } from '../../../core/api/account-settings/accounts-settings.service';
import { BidiService } from '../../../core/i18n/bidi.service';
import { MatChipsModule } from '@angular/material/chips';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { CheckMoreTooltipDirective } from '../../checkMoreTooltip.directive';

export enum ResourceType {
  Taxonomy = 'taxonomies',
  ArticleType = 'articleType',
}

@Component({
  selector: 'gd-chip-list',
  templateUrl: './chip-list.component.html',
  styleUrls: ['./chip-list.component.scss'],
  imports: [
    CommonModule,
    MatChipsModule,
    MatTooltipModule,
    MatIconModule,
    CheckMoreTooltipDirective,
  ],
})

export class ChipListComponent implements OnInit {
  @Input() items;
  @Input() resource;
  @Input() contentLocaleId;
  @Input() width: number = 390;
  @Input() visibleRows: number | null = null;
  @Input() fontSize: string = '12px';
  @Input() resourceType: ResourceType = ResourceType.Taxonomy;

  ResourceType = ResourceType;
  showTaxonomyPath: boolean = this.accountSettingsService.getShowTaxonomyPathFlag();
  dir$ = this.bidiService.getEffectiveLocaleDirectionality();
  preparedData;

  constructor(
    private accountSettingsService: AccountSettingsService,
    private bidiService: BidiService
  ) {}

  ngOnInit() {
    this.items = this.items?.map(item => {
      return {...item, displayData: item.taxonomyDisplayData || [item.name]};
    });
    this.preparedData = this.prepareDataForDisplay(
      this.items,
      this.contentLocaleId || this.resource.contentLocaleId || this.resource?.contentLocale?.id || null,
      this.width,
      this.visibleRows
    );
  }

  // This function is used to prepare display data for Article list taxonomy cell.
  // Taxonomy name length is used to calculate the maximum number of displayed elements.
  prepareDataForDisplay(
    items,
    contentLocaleId = null,
    width: number,
    visibleRows: number | null = 2
  ) {
    let chipWrapperLength = 0;
    let row = 0;

    const visibleChips = [];
    const hiddenChips = [];

    items?.forEach((item) => {
      if (visibleRows === null) {
        return visibleChips.push(item);
      }
      if (row > visibleRows) {
        return hiddenChips.push(item);
      }
      const pathLength =
        this.showTaxonomyPath && this.resourceType === ResourceType.Taxonomy
          ? this.fullPathLength(item.displayData)
          : this.pathLength(item.name);
      chipWrapperLength += pathLength;
      const maxLength = row < visibleRows ? width : width - 30;
      if (chipWrapperLength < maxLength) {
        return visibleChips.push(item);
      }
      row += 1;
      if (row < visibleRows + 1 && visibleRows) {
        chipWrapperLength = pathLength;
        return visibleChips.push(item);
      }
      hiddenChips.push(item);
    });

    const preparedData = {
      visibleChips,
      hiddenChips,
      infoChip: hiddenChips.length > 0 ? { id: 'notShown', name: '+' + hiddenChips.length } : null,
    };
    return preparedData;
  }

  fullPathLength(names, charInPx = 6) {
    const chipMargins = names?.length > 3 ? 90 : (names?.length - 1) * 25 + 20;
    const pathLength = names?.reduce((obj, item) => {
      if (item !== 'NODE_PLACEHOLDER') {
        obj += item?.length;
      }
      return obj;
    }, 0);
    return pathLength * charInPx + chipMargins;
  }

  pathLength(name, charInPx = 6, chipMargins = 25) {
    return name.length * charInPx + chipMargins;
  }
}
